import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

import FuncionarioLogin from "./components/Auth/FuncionarioLogin";
import Login from "./components/Auth/LoginModern";
import PrivacyPolicy from "./components/Common/PrivacyPolicy";
import TermsOfService from "./components/Common/TermsOfService";
import CadastroMedidas from "./components/Funcionarios/CadastroMedidas/CadastroMedidas";
import LandingPage from "./components/LandingPage/LandingPage";
import PublicReservaView from "./components/Reservas/PublicReservaView";
import { useAuth } from "./hooks/useAuth";
import { onAuthStateChange } from "./services/authService";
import { Funcionario } from "./types";

import { CustomModal } from "./components/Common/Modal";
import PrivateRoutes from "./routes/PrivateRoutes";
import { initUser } from "./services/userService";

function AppContent() {
  const { user, funcionario, setUser, setFuncionario, logout } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [showPrivacyModal, setShowPrivacyModal] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showFuncionarioLogin, setShowFuncionarioLogin] =
    useState<boolean>(false);
  const [showLandingPage, setShowLandingPage] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (u) => {
      setUser(u);
      if (u) {
        await initUser(u);
        // Se o usuário está logado, pular a landing page
        setShowLandingPage(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser]);

  const handleLogout = async () => {
    try {
      const { logoutUser } = await import("./services/authService");
      await logoutUser();
      logout();
      // Ao fazer logout, mostrar a landing page novamente
      setShowLandingPage(true);
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const handleFuncionarioLogin = (funcionarioData: Funcionario) => {
    setFuncionario(funcionarioData);
    setShowFuncionarioLogin(false);
  };

  const handleBackToMain = () => {
    setShowFuncionarioLogin(false);
    setShowLandingPage(true); // Voltar para landing page ao sair do login de funcionário
  };

  const handleFuncionarioLogout = () => {
    setFuncionario(null);
    setShowLandingPage(true); // Voltar para landing page ao fazer logout de funcionário
  };

  const renderLegalModals = () => (
    <>
      {showPrivacyModal && (
        <CustomModal
          show={showPrivacyModal}
          onHide={() => setShowPrivacyModal(false)}
          headerLabel="Política de Privacidade"
          size="xl"
        >
          <PrivacyPolicy />
        </CustomModal>
      )}

      {showTermsModal && (
        <CustomModal
          show={showTermsModal}
          onHide={() => setShowTermsModal(false)}
          headerLabel="Termos de Uso"
          size="xl"
        >
          <TermsOfService />
        </CustomModal>
      )}
    </>
  );

  // Fallback de rota pública: permite abrir /reserva/:id sem login
  // mesmo quando a aplicação estiver sob um prefixo de URL.
  const isPublicReservaPath = /\/reserva\/[^/]+/.test(location.pathname);
  if (isPublicReservaPath) {
    return <PublicReservaView />;
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3 text-muted">Carregando sistema...</p>
        </div>
      </div>
    );
  }

  // Se funcionário estiver logado, mostrar tela de cadastro de medidas
  if (funcionario && funcionario.id) {
    return (
      <CadastroMedidas
        funcionario={funcionario}
        onLogout={handleFuncionarioLogout}
        userId={funcionario.userId} // Usar o userId do admin que está no funcionário
      />
    );
  }

  // Se estiver mostrando login de funcionário
  if (showFuncionarioLogin) {
    return (
      <FuncionarioLogin
        onLoginSuccess={handleFuncionarioLogin}
        onBackToMain={handleBackToMain}
      />
    );
  }

  // Se não estiver logado, mostrar landing page ou tela de login
  if (!user) {
    // Mostrar landing page primeiro
    if (showLandingPage) {
      return (
        <>
          <LandingPage
            onNavigateToLogin={() => setShowLandingPage(false)}
            onNavigateToFuncionarioLogin={() => {
              setShowLandingPage(false);
              setShowFuncionarioLogin(true);
            }}
            onOpenPrivacy={() => setShowPrivacyModal(true)}
            onOpenTerms={() => setShowTermsModal(true)}
          />
          {renderLegalModals()}
        </>
      );
    }

    // Mostrar tela de login
    return (
      <>
        <div className="position-relative vh-100">
          <Login
            onLoginSuccess={setUser}
            onFuncionarioLoginSuccess={handleFuncionarioLogin}
            onShowFuncionarioLogin={() => setShowFuncionarioLogin(true)}
          />
        </div>
        {renderLegalModals()}
      </>
    );
  }

  return <PrivateRoutes onLogout={handleLogout} />;
}

function App() {
  return (
    <Routes>
      {/* Rota pública para visualização de reserva */}
      <Route path="/reserva/:id" element={<PublicReservaView />} />

      {/* Fluxo principal da aplicação (com autenticação quando necessário) */}
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
}

export default App;
